import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import Topbar from "./scenes/global/Topbar.jsx";
import Sidebar from "./scenes/global/Sidebar.jsx";
import Footer from "./scenes/global/Footer.jsx";
import Dashboard from "./scenes/dashboard/index.jsx";
import DecDashboard from "./scenes/dec-dashboard/index.jsx";
import Market from "./scenes/market/index.jsx";
import LandMarket from "./scenes/market/LandMarket.jsx";
import MarketItems from "./scenes/market/MarketItems.jsx";
import MyBidsPage from "./scenes/market/MyBidsPage.jsx";
import MyCollection from "./scenes/market/MyCollection.jsx";
import CardWinratesPage from "./scenes/card-winrates/index.jsx";
import { Box, CssBaseline, ThemeProvider, Fab, Tooltip } from "@mui/material";
import { ColorModeContext, useMode } from "./theme.js";
import priceService from "./services/PriceService.js";
import BurnTrackerService from "./services/BurnTrackerService.js";
import { CartProvider } from './contexts/CartContext.js';
import { LandCartProvider } from './contexts/LandCartContext.js';
import { ItemCartProvider } from './contexts/ItemCartContext.js';
import { ListingProvider } from './contexts/ListingContext.js';

// Operator tool for the Splinterlands bot, development only.
//
// require() rather than a top-level import, and guarded by NODE_ENV, so the
// module and everything it pulls in stay OUT of the production bundle - `npm run
// build` sets NODE_ENV=production, the branch folds to false and the dependency is
// dropped. A static `import` would be bundled regardless of any runtime check.
// Deliberately not linked from the sidebar: reachable only by typing /bot.
const BotDashboard =
  process.env.NODE_ENV === 'development'
    ? require('./scenes/bot/index.jsx').default
    : null;

// Dasselbe fuer den Snipe-Bot: privates Werkzeug, nicht Teil der Website. Erreichbar
// nur durch Eingabe von /snipe.
const SnipeDashboard =
  process.env.NODE_ENV === 'development'
    ? require('./scenes/snipe/index.jsx').default
    : null;


function App() {
  const [theme, colorMode] = useMode();
  const location = useLocation();
  // Sidebar standardmäßig eingeklappt außer auf /market Routes
  const [isSidebar, setIsSidebar] = useState(location.pathname.startsWith('/market'));
  const [burnModalOpen, setBurnModalOpen] = useState(false);
  const [burnEvent, setBurnEvent] = useState(null);
  const [donationEvent, setDonationEvent] = useState(null);
  const [isFlywheelActive, setIsFlywheelActive] = useState(false);
  const [lastBurnAmount, setLastBurnAmount] = useState(0);
  const [lastBurnTimestamp, setLastBurnTimestamp] = useState(null);
  const [lastDonationTimestamp, setLastDonationTimestamp] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const initialLoadRef = useRef(true);
  
  // Use throttled state updates to reduce render frequency
  const updateFlywheelStatus = useCallback((isActive) => {
    // Only update if the value actually changed
    setIsFlywheelActive(prev => {
      if (prev !== isActive) return isActive;
      return prev;
    });
  }, []);

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      // Auto-close sidebar on small screens
      if (window.innerWidth < 768) {
        setIsSidebar(false);
      } else {
        // On larger screens, open sidebar only on /market routes
        setIsSidebar(location.pathname.startsWith('/market'));
      }
    };
    
    window.addEventListener("resize", handleResize);
    handleResize(); // Initialize on first load
    
    // Inject viewport meta tag for proper mobile scaling
    const meta = document.createElement('meta');
    meta.name = 'viewport';
    meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
    document.getElementsByTagName('head')[0].appendChild(meta);
    
    return () => window.removeEventListener("resize", handleResize);
  }, [location.pathname]); // Re-run when route changes

  // BurnTrackerService beim App-Start initialisieren
  useEffect(() => {
    // BurnTrackerService initialisieren
    BurnTrackerService.init();
    
    // Auf Burn-Events reagieren, die über das UI ausgelöst werden oder von Chart-Updates kommen
    const unsubscribe = priceService.subscribeToBurnEvents((event) => {
      // Prüfen, ob es ein neues Event ist und nicht der erste Seitenload
      const isDifferentEvent = !lastBurnTimestamp || 
                               event.spsBurned !== lastBurnAmount ||
                               (new Date() - new Date(lastBurnTimestamp)) > 5000;
      
      // Animation nicht beim ersten Laden der Seite auslösen
      if (isDifferentEvent && !initialLoadRef.current) {
        setLastBurnAmount(event.spsBurned);
        setLastBurnTimestamp(event.timestamp || new Date().toISOString());
        setBurnEvent(event);
        
        // Nach 6 Sekunden zurücksetzen (Länge der Animation)
        setTimeout(() => {
          setBurnEvent(null);
        }, 6000);
      }
      
      // Nach dem ersten Event-Empfang ist es kein initiales Laden mehr
      if (initialLoadRef.current) {
        initialLoadRef.current = false;
        
        // Initialwerte setzen ohne Animation auszulösen
        if (event.spsBurned) {
          setLastBurnAmount(event.spsBurned);
        }
        if (event.timestamp) {
          setLastBurnTimestamp(event.timestamp);
        }
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, [lastBurnAmount, lastBurnTimestamp]);

  // Service für Donation-Events einrichten
  useEffect(() => {
    // Eigene Methode für Donation-Events im priceService hinzufügen
    if (!priceService.subscribeToDonationEvents) {
      priceService.donationCallbacks = [];
      priceService.registerDonationEvent = (data) => {
        priceService.donationCallbacks.forEach(callback => callback(data));
      };
      priceService.subscribeToDonationEvents = (callback) => {
        priceService.donationCallbacks.push(callback);
        return () => {
          priceService.donationCallbacks = priceService.donationCallbacks.filter(cb => cb !== callback);
        };
      };
    }

    // Auf Donation-Events hören
    const unsubscribe = priceService.subscribeToDonationEvents((event) => {
      const isDifferentEvent = !lastDonationTimestamp || 
                              (new Date() - new Date(lastDonationTimestamp)) > 5000;
      
      if (isDifferentEvent) {
        setLastDonationTimestamp(event.timestamp || new Date().toISOString());
        setDonationEvent(event);
        
        // Nach 6 Sekunden zurücksetzen
        setTimeout(() => {
          setDonationEvent(null);
        }, 6000);
      }
    });
    
    return () => unsubscribe();
  }, [lastDonationTimestamp]);
  
  // Auf Preis- und Flywheel-Status reagieren
  useEffect(() => {
    let lastUpdateTime = 0;
    const throttleTime = 5000; // Update at most every 5 seconds
    
    const unsubscribe = priceService.subscribe((data) => {
      const now = Date.now();
      if (now - lastUpdateTime > throttleTime) {
        lastUpdateTime = now;
        updateFlywheelStatus(data.isFlywheelActive);
      }
    });
    
    return () => unsubscribe();
  }, [updateFlywheelStatus]);
  

  // Memoize the routes to prevent re-rendering when parent re-renders
  const routes = useMemo(() => (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/dec" element={<DecDashboard />} />
      <Route path="/market" element={<Market setIsSidebar={setIsSidebar} isMobile={isMobile} />} />
      <Route path="/market/land" element={<LandMarket setIsSidebar={setIsSidebar} isMobile={isMobile} />} />
      <Route path="/market/items" element={<MarketItems setIsSidebar={setIsSidebar} isMobile={isMobile} />} />
      <Route path="/market/bids" element={<MyBidsPage />} />
      <Route path="/market/mycards" element={<MyCollection setIsSidebar={setIsSidebar} isMobile={isMobile} />} />
      <Route path="/card-winrates" element={<CardWinratesPage />} />
      {BotDashboard && <Route path="/bot" element={<BotDashboard />} />}
      {SnipeDashboard && <Route path="/snipe" element={<SnipeDashboard />} />}
    </Routes>
  ), [setIsSidebar, isMobile]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className="app">
          <Sidebar isSidebar={isSidebar} setIsSidebar={setIsSidebar} isMobile={isMobile} />
          <main className="content">
            <Topbar setIsSidebar={setIsSidebar} isMobile={isMobile} />
            <Box m="20px">
              {routes}
            </Box>
            <Footer />
          </main>
        </div>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

// Wrap the app with CartProvider, LandCartProvider, ItemCartProvider, and ListingProvider
const AppWithProviders = () => (
  <CartProvider>
    <LandCartProvider>
      <ItemCartProvider>
        <ListingProvider>
          <App />
        </ListingProvider>
      </ItemCartProvider>
    </LandCartProvider>
  </CartProvider>
);

export default AppWithProviders;
